// src/services/telemetry.js
/**
 * Enhanced Telemetry Service - Centralized monitoring and logging
 * 
 * Collections:
 * - cart_events: fine-grained user actions in cart and validation errors
 * - cart_submissions: per-checkout attempt with payload and outcomes
 * - odin_logs: arbitrary logs from admin tools
 * - performance_metrics: performance monitoring data
 * - error_logs: application errors and exceptions
 * - user_interactions: user behavior tracking
 * - api_calls: API request/response logging
 * 
 * Enhanced Features:
 * - Performance monitoring
 * - Error tracking with context
 * - User interaction analytics
 * - API call logging
 * - Session tracking
 * - Real-time metrics
 */

import { db, auth } from './firebase';
import {
    addDoc,
    collection,
    doc,
    serverTimestamp,
    setDoc,
    updateDoc,
    query,
    where,
    getDocs,
    writeBatch,
    limit,
    Timestamp,
    FieldValue,
    deleteField,
} from 'firebase/firestore';
import sendMailMergeEmail from '../pages/ScaleComponents/emailtransporter';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

export const LOG_LEVELS = {
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
    CRITICAL: 'critical'
};

export const COLLECTIONS = {
    CART_EVENTS: 'cart_events',
    CART_SUBMISSIONS: 'cart_submissions',
    ODIN_LOGS: 'odin_logs',
    PERFORMANCE_METRICS: 'performance_metrics',
    ERROR_LOGS: 'error_logs',
    USER_INTERACTIONS: 'user_interactions',
    API_CALLS: 'api_calls',
    FAILED_ORDERS: 'failed_orders',
    CART_SNAPSHOTS: 'cart_snapshots'
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

function safeJson(val, max = 200_000) {
    try {
        const s = JSON.stringify(val ?? null);
        return s.length > max ? s.slice(0, max) + ` …(+${s.length - max} bytes)` : s;
    } catch {
        return String(val);
    }
}

function getContext() {
    if (typeof window === 'undefined') return {};
    
    return {
        url: window.location.href,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
        timestamp: new Date().toISOString()
    };
}

/** Current logged-in account (for error logs: who generated the error). */
function getCurrentAccount() {
    try {
        const user = auth?.currentUser;
        if (!user) return {};
        return {
            accountUserId: user.uid,
            accountUserEmail: user.email || null,
            accountDisplayName: user.displayName || null,
        };
    } catch {
        return {};
    }
}

function isPlainObject(o) {
    if (o === null || typeof o !== 'object') return false;
    return Object.getPrototypeOf(o) === Object.prototype;
}

/**
 * Firestore rejects `undefined` anywhere in document data. Removes undefined
 * keys recursively; preserves Date, Timestamp, FieldValue, and non-plain objects
 * (e.g. GeoPoint, DocumentReference).
 * @param {*} value
 * @param {number} [depth]
 * @returns {*}
 */
export function sanitizeForFirestore(value, depth = 0) {
    if (depth > 32) return null;
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (typeof value === 'number' && Number.isNaN(value)) return null;
    if (typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number') {
        return value;
    }
    if (typeof value === 'bigint') return String(value);
    if (value instanceof Date) return value;
    if (value instanceof Timestamp || value?.constructor?.name === 'Timestamp') return value;
    if (value instanceof FieldValue || value?.constructor?.name === 'FieldValue') return value;

    if (Array.isArray(value)) {
        return value
            .map((item) => sanitizeForFirestore(item, depth + 1))
            .filter((item) => item !== undefined);
    }

    if (!isPlainObject(value)) {
        return value;
    }

    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (v === undefined) continue;
        const next = sanitizeForFirestore(v, depth + 1);
        if (next !== undefined) out[k] = next;
    }
    return out;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CART TELEMETRY (Original functionality)
// ═══════════════════════════════════════════════════════════════════════════════

export async function logCartEvent(event) {
    const payload = sanitizeForFirestore({
        ...event,
        context: getContext(),
        createdAt: serverTimestamp(),
    });
    try {
        await addDoc(collection(db, COLLECTIONS.CART_EVENTS), payload);
    } catch (e) {
        console.warn('[telemetry] logCartEvent failed', e);
    }
}

export async function beginCartSubmission(init) {
    const base = sanitizeForFirestore({
        status: 'initiated',
        createdAt: serverTimestamp(),
        companyName: init?.companyName || '',
        userId: init?.userId || '',
        userEmail: init?.userEmail || '',
        cartTotal: init?.cartTotal ?? 0,
        cartItems: init?.cartItems || [],
        deliveryDate: init?.deliveryDate || null,
        isCollection: !!init?.isCollection,
        purchaseOrder: init?.purchaseOrder || '',
        payloadPreview: safeJson(init?.payloadPreview),
        clientMeta: init?.clientMeta || {},
        context: getContext()
    });
    try {
        const ref = await addDoc(collection(db, COLLECTIONS.CART_SUBMISSIONS), base);
        return ref.id;
    } catch (e) {
        console.warn('[telemetry] beginCartSubmission failed', e);
        return null;
    }
}

export async function updateCartSubmission(submissionId, patch) {
    if (!submissionId) return;
    try {
        const cleaned = sanitizeForFirestore({ ...patch, updatedAt: serverTimestamp() });
        await updateDoc(doc(db, COLLECTIONS.CART_SUBMISSIONS, submissionId), cleaned);
    } catch (e) {
        console.warn('[telemetry] updateCartSubmission failed', e);
    }
}

export async function completeCartSubmission(submissionId, result) {
    if (!submissionId) return;
    const patch = {
        status: result?.status || 'completed',
        outcome: result?.outcome || 'success',
        orderId: result?.orderId || null,
        orderDeskResponse: safeJson(result?.orderDeskResponse),
        error: result?.error ? String(result.error) : null,
        finishedAt: serverTimestamp(),
    };
    return updateCartSubmission(submissionId, patch);
}

/**
 * Capture failed order for admin recovery
 */
export async function captureFailedOrder(orderData, error, context = {}) {
    const payload = sanitizeForFirestore({
        // Order essentials
        cartItems: orderData.cartItems || [],
        deliveryDate: orderData.deliveryDate || null,
        isCollection: orderData.isCollection || false,
        purchaseOrder: orderData.purchaseOrder || '',
        orderNote: orderData.orderNote || '',
        
        // User data
        userId: orderData.userId || 'anonymous',
        userEmail: orderData.userEmail || '',
        companyName: orderData.companyName || '',
        userData: orderData.userData || {},
        
        // Address data
        selectedAddress: orderData.selectedAddress || null,
        selectedBillingAddress: orderData.selectedBillingAddress || null,
        
        // Financial
        subtotal: orderData.subtotal || 0,
        shipping: orderData.shipping || 0,
        total: orderData.total || 0,
        
        // Error details
        errorMessage: error?.message || String(error),
        errorStack: error?.stack || null,
        failurePoint: context.failurePoint || 'unknown',
        attemptedOrderId: context.attemptedOrderId || null,
        
        // Metadata
        status: 'pending_admin_review',
        attemptCount: 1,
        context: getContext(),
        createdAt: serverTimestamp(),
        lastAttemptAt: serverTimestamp(),
        
        // Submission tracking
        submissionId: context.submissionId || null
    });
    
    try {
        const ref = await addDoc(collection(db, COLLECTIONS.FAILED_ORDERS), payload);
        console.log(`[telemetry] Failed order captured: ${ref.id}`);
        
        // Send email notification to dev team
        try {
            const devEmail = 'digital@londonsmokeandcure.co.uk';
            const companyName = orderData.companyName || 'Unknown Company';
            const userEmail = orderData.userEmail || 'No email';
            const orderValue = Number(orderData.total || 0).toFixed(2);
            const itemCount = (orderData.cartItems || []).length;
            const failurePoint = context.failurePoint || 'unknown';
            const errorMsg = error?.message || String(error) || 'Unknown error';
            
            // Format delivery date
            const deliveryDate = orderData.deliveryDate 
                ? new Date(orderData.deliveryDate).toLocaleDateString('en-GB')
                : 'Not specified';
            
            // Build item summary
            const itemSummary = (orderData.cartItems || [])
                .slice(0, 10)
                .map(item => {
                    const qty = item.quantity || item.qty || 0;
                    const name = item.name || item.sku || 'Unknown';
                    return `  • ${qty}× ${name}`;
                })
                .join('\n');
            const moreItems = (orderData.cartItems || []).length > 10 
                ? `\n  ... and ${(orderData.cartItems || []).length - 10} more items` 
                : '';
            
            // Format address if available
            const addressText = orderData.selectedAddress
                ? `${orderData.selectedAddress.line1 || ''}\n${orderData.selectedAddress.line2 || ''}\n${orderData.selectedAddress.city || ''} ${orderData.selectedAddress.postCode || ''}`.trim()
                : 'No address provided';
            
            const subject = `🚨 Failed Order Alert: ${companyName} - £${orderValue}`;
            
            const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #dc3545; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9f9f9; padding: 20px; border: 1px solid #ddd; }
        .alert-box { background: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0; }
        .info-box { background: white; border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 4px; }
        .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
        .value { font-size: 24px; font-weight: bold; color: #dc3545; }
        code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🚨 Failed Order Alert</h1>
        </div>
        <div class="content">
            <div class="alert-box">
                <p style="margin: 0 0 10px 0;"><strong>⚠️ A customer order has failed to submit</strong></p>
                <p style="margin: 0;">This order requires immediate attention in the Admin Dashboard.</p>
            </div>
            
            <div class="info-box">
                <h3 style="margin-top: 0;">Order Details</h3>
                <p><strong>Company:</strong> ${companyName}</p>
                <p><strong>Customer Email:</strong> ${userEmail}</p>
                <p><strong>Order Value:</strong> <span class="value">£${orderValue}</span></p>
                <p><strong>Items:</strong> ${itemCount}</p>
                <p><strong>Delivery Date:</strong> ${deliveryDate}</p>
                <p><strong>Collection:</strong> ${orderData.isCollection ? 'Yes' : 'No'}</p>
                ${orderData.purchaseOrder ? `<p><strong>PO Number:</strong> ${orderData.purchaseOrder}</p>` : ''}
            </div>
            
            <div class="info-box">
                <h3 style="margin-top: 0;">Failure Information</h3>
                <p><strong>Failure Point:</strong> <code>${failurePoint}</code></p>
                <p><strong>Error Message:</strong></p>
                <code style="display: block; padding: 10px; margin: 10px 0; background: #f4f4f4; border-radius: 4px; white-space: pre-wrap;">${errorMsg}</code>
                ${context.attemptedOrderId ? `<p><strong>Attempted Order ID:</strong> ${context.attemptedOrderId}</p>` : ''}
                <p><strong>Failed Order ID:</strong> ${ref.id}</p>
            </div>
            
            ${itemSummary ? `
            <div class="info-box">
                <h3 style="margin-top: 0;">Cart Items</h3>
                <pre style="margin: 0; font-family: Arial, sans-serif; white-space: pre-wrap;">${itemSummary}${moreItems}</pre>
            </div>
            ` : ''}
            
            ${orderData.selectedAddress ? `
            <div class="info-box">
                <h3 style="margin-top: 0;">Delivery Address</h3>
                <pre style="margin: 0; font-family: Arial, sans-serif; white-space: pre-wrap;">${addressText}</pre>
            </div>
            ` : ''}
            
            ${orderData.orderNote ? `
            <div class="info-box">
                <h3 style="margin-top: 0;">Order Note</h3>
                <p style="margin: 0; font-style: italic; color: #666;">${orderData.orderNote}</p>
            </div>
            ` : ''}
            
            <div style="margin-top: 20px; padding: 15px; background: #e7f3ff; border-left: 4px solid #1890ff; border-radius: 4px;">
                <p style="margin: 0;"><strong>Action Required:</strong></p>
                <p style="margin: 10px 0 0 0;">Please review this failed order in the Admin Dashboard → Failed Orders section and attempt recovery.</p>
            </div>
        </div>
        <div class="footer">
            <p>This is an automated alert from the Wholesale Ordering System.</p>
            <p>Failed Order ID: ${ref.id}</p>
        </div>
    </div>
</body>
</html>`;
            
            const text = `
🚨 FAILED ORDER ALERT

A customer order has failed to submit and requires immediate attention.

ORDER DETAILS:
Company: ${companyName}
Customer Email: ${userEmail}
Order Value: £${orderValue}
Items: ${itemCount}
Delivery Date: ${deliveryDate}
Collection: ${orderData.isCollection ? 'Yes' : 'No'}
${orderData.purchaseOrder ? `PO Number: ${orderData.purchaseOrder}\n` : ''}

FAILURE INFORMATION:
Failure Point: ${failurePoint}
Error Message: ${errorMsg}
${context.attemptedOrderId ? `Attempted Order ID: ${context.attemptedOrderId}\n` : ''}Failed Order ID: ${ref.id}

${itemSummary ? `CART ITEMS:\n${itemSummary}${moreItems}\n\n` : ''}${orderData.selectedAddress ? `DELIVERY ADDRESS:\n${addressText}\n\n` : ''}${orderData.orderNote ? `ORDER NOTE:\n${orderData.orderNote}\n\n` : ''}ACTION REQUIRED:
Please review this failed order in the Admin Dashboard → Failed Orders section and attempt recovery.

Failed Order ID: ${ref.id}
`;
            
            await sendMailMergeEmail({
                to: devEmail,
                subject,
                html,
                text,
                templateKey: 'failed_order_alert',
                sourcePath: 'src/services/telemetry.js:captureFailedOrder',
                context: { 
                    failedOrderId: ref.id,
                    companyName,
                    orderValue,
                    failurePoint
                },
                includeAccountAdditionalEmails: false,
            });
            
            console.log(`[telemetry] Failed order alert email sent to ${devEmail}`);
        } catch (emailError) {
            // Don't fail the capture if email fails - just log it
            console.error('[telemetry] Failed to send failed order alert email:', emailError);
        }
        
        return ref.id;
    } catch (e) {
        console.error('[telemetry] Failed to capture failed order:', e);
        return null;
    }
}

/**
 * Save cart snapshot for Cart Reports / last-seen. Authenticated users: one document per user
 * (`cart_snapshots/{uid}`) with merge so cart edits update the same row and admin nudge metadata is preserved.
 */
export async function saveCartSnapshot(cartData) {
    const validationError =
        cartData.validationError != null && cartData.validationError !== ''
            ? String(cartData.validationError)
            : null;

    const uid = cartData.userId || 'anonymous';
    const base = {
        userId: uid,
        userEmail: cartData.userEmail ?? '',
        companyName: cartData.companyName ?? '',
        cartItems: Array.isArray(cartData.cartItems) ? cartData.cartItems : [],
        deliveryDate: cartData.deliveryDate ?? null,
        isCollection: !!cartData.isCollection,
        total: cartData.total != null && !Number.isNaN(Number(cartData.total)) ? Number(cartData.total) : 0,
        context: getContext(),
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
    };

    try {
        if (uid && uid !== 'anonymous') {
            const payload = sanitizeForFirestore({
                ...base,
                ...(validationError ? { validationError } : { validationError: deleteField() }),
            });
            await setDoc(doc(db, COLLECTIONS.CART_SNAPSHOTS, uid), payload, { merge: true });
            return uid;
        }
        const docId = `anon_${Date.now()}`;
        const payload = sanitizeForFirestore({
            ...base,
            ...(validationError ? { validationError } : {}),
        });
        await setDoc(doc(db, COLLECTIONS.CART_SNAPSHOTS, docId), payload);
        return docId;
    } catch (e) {
        console.warn('[telemetry] saveCartSnapshot failed', e);
        return null;
    }
}

/**
 * Remove all cart_snapshots for a user (Firestore Cart Reports source).
 * Call after a successful order submit or when the customer clears their cart so admin views stay current.
 * Requires Firestore rules allowing list/delete on own docs (see firestore.rules cart_snapshots).
 */
export async function deleteCartSnapshotsForUser(userId) {
    if (!userId || userId === 'anonymous') return { deleted: 0 };

    const tryDeleteBatch = async () => {
        const q = query(
            collection(db, COLLECTIONS.CART_SNAPSHOTS),
            where('userId', '==', userId),
            limit(500)
        );
        const snap = await getDocs(q);
        if (snap.empty) return 0;

        let batch = writeBatch(db);
        let n = 0;
        snap.docs.forEach((d) => {
            batch.delete(doc(db, COLLECTIONS.CART_SNAPSHOTS, d.id));
            n++;
        });
        await batch.commit();
        return n;
    };

    try {
        let total = 0;
        for (let round = 0; round < 25; round++) {
            const n = await tryDeleteBatch();
            total += n;
            if (n < 500) break;
        }
        if (total > 0) {
            console.log(`[telemetry] deleteCartSnapshotsForUser: removed ${total} snapshot(s) for ${userId}`);
        }
        return { deleted: total };
    } catch (e) {
        console.warn('[telemetry] deleteCartSnapshotsForUser failed', e);
        return { deleted: 0, error: e };
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ODIN LOGGING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Log ODIN dashboard events
 */
export async function logOdin(message, level = LOG_LEVELS.INFO, meta = {}) {
    const row = sanitizeForFirestore({
        message,
        level,
        meta,
        context: getContext(),
        createdAt: serverTimestamp(),
    });
    try {
        await addDoc(collection(db, COLLECTIONS.ODIN_LOGS), row);
    } catch (e) {
        console.warn('[telemetry] logOdin failed', e);
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// PERFORMANCE MONITORING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Log performance metric
 */
export async function logPerformance(metricName, value, metadata = {}) {
    const payload = sanitizeForFirestore({
        metricName,
        value,
        metadata,
        context: getContext(),
        createdAt: serverTimestamp()
    });
    
    try {
        await addDoc(collection(db, COLLECTIONS.PERFORMANCE_METRICS), payload);
    } catch (e) {
        console.warn('[telemetry] logPerformance failed', e);
    }
}

/**
 * Measure and log function execution time
 */
export async function measurePerformance(name, fn) {
    const startTime = performance.now();
    let result;
    let error = null;
    
    try {
        result = await fn();
    } catch (e) {
        error = e;
    }
    
    const duration = performance.now() - startTime;
    
    await logPerformance(name, duration, {
        success: !error,
        error: error ? String(error) : null
    });
    
    if (error) throw error;
    return result;
}

/**
 * Log page load performance
 */
export async function logPageLoad() {
    if (typeof window === 'undefined' || !window.performance) return;
    
    const perfData = window.performance.timing;
    const loadTime = perfData.loadEventEnd - perfData.navigationStart;
    const domReadyTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;
    
    await logPerformance('page_load', loadTime, {
        domReadyTime,
        redirectTime: perfData.redirectEnd - perfData.redirectStart,
        dnsTime: perfData.domainLookupEnd - perfData.domainLookupStart,
        connectTime: perfData.connectEnd - perfData.connectStart,
        requestTime: perfData.responseStart - perfData.requestStart,
        responseTime: perfData.responseEnd - perfData.responseStart,
        renderTime: perfData.domComplete - perfData.domLoading
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Log application error.
 * additionalContext can include: context, source, service, companyName, userEmail, userId.
 * Current account (from auth) is attached automatically so Odin shows which account generated the error.
 */
export async function logError(error, additionalContext = {}) {
    const payload = sanitizeForFirestore({
        errorMessage: error?.message || String(error),
        errorStack: error?.stack || 'No stack trace',
        errorName: error?.name || 'Error',
        additionalContext,
        context: getContext(),
        level: LOG_LEVELS.ERROR,
        createdAt: serverTimestamp(),
        ...getCurrentAccount(),
        ...(additionalContext.companyName && { companyName: additionalContext.companyName }),
        ...(additionalContext.userEmail && { userEmail: additionalContext.userEmail }),
        ...(additionalContext.userId && { userId: additionalContext.userId }),
    });
    
    try {
        await addDoc(collection(db, COLLECTIONS.ERROR_LOGS), payload);
    } catch (e) {
        console.warn('[telemetry] logError failed', e);
    }
}

/**
 * Log critical error (includes current account for Odin reporting).
 */
export async function logCriticalError(error, additionalContext = {}) {
    const payload = sanitizeForFirestore({
        errorMessage: error?.message || String(error),
        errorStack: error?.stack || 'No stack trace',
        errorName: error?.name || 'Error',
        additionalContext,
        context: getContext(),
        level: LOG_LEVELS.CRITICAL,
        createdAt: serverTimestamp(),
        ...getCurrentAccount(),
        ...(additionalContext.companyName && { companyName: additionalContext.companyName }),
        ...(additionalContext.userEmail && { userEmail: additionalContext.userEmail }),
        ...(additionalContext.userId && { userId: additionalContext.userId }),
    });
    
    try {
        await addDoc(collection(db, COLLECTIONS.ERROR_LOGS), payload);
    } catch (e) {
        console.error('[telemetry] logCriticalError failed', e);
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// USER INTERACTION TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Log user interaction
 */
export async function logInteraction(action, target, metadata = {}) {
    const payload = sanitizeForFirestore({
        action,
        target,
        metadata,
        context: getContext(),
        createdAt: serverTimestamp()
    });
    
    try {
        await addDoc(collection(db, COLLECTIONS.USER_INTERACTIONS), payload);
    } catch (e) {
        console.warn('[telemetry] logInteraction failed', e);
    }
}

/**
 * Log button click
 */
export async function logButtonClick(buttonName, metadata = {}) {
    return logInteraction('button_click', buttonName, metadata);
}

/**
 * Log page view
 */
export async function logPageView(pageName, metadata = {}) {
    return logInteraction('page_view', pageName, metadata);
}

/**
 * Log feature usage
 */
export async function logFeatureUsage(featureName, metadata = {}) {
    return logInteraction('feature_usage', featureName, metadata);
}

// ═══════════════════════════════════════════════════════════════════════════════
// API CALL LOGGING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Log API call
 */
export async function logAPICall(endpoint, method, status, duration, metadata = {}) {
    const payload = sanitizeForFirestore({
        endpoint,
        method,
        status,
        duration,
        success: status >= 200 && status < 300,
        metadata,
        context: getContext(),
        createdAt: serverTimestamp()
    });
    
    try {
        await addDoc(collection(db, COLLECTIONS.API_CALLS), payload);
    } catch (e) {
        console.warn('[telemetry] logAPICall failed', e);
    }
}

/**
 * Wrap API call with logging
 */
export async function trackAPICall(endpoint, method, apiCallFn) {
    const startTime = performance.now();
    let response;
    let error = null;
    
    try {
        response = await apiCallFn();
    } catch (e) {
        error = e;
    }
    
    const duration = performance.now() - startTime;
    const status = response?.status || (error ? 0 : 200);
    
    await logAPICall(endpoint, method, status, duration, {
        error: error ? String(error) : null
    });
    
    if (error) throw error;
    return response;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SESSION TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

let sessionId = null;
let sessionStartTime = null;

/**
 * Initialize session tracking
 */
export function initSession(userId = null) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStartTime = Date.now();
    
    return sessionId;
}

/**
 * Get current session ID
 */
export function getSessionId() {
    if (!sessionId) {
        initSession();
    }
    return sessionId;
}

/**
 * Get session duration in milliseconds
 */
export function getSessionDuration() {
    if (!sessionStartTime) return 0;
    return Date.now() - sessionStartTime;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export default {
    sanitizeForFirestore,
    // Original cart telemetry
    logCartEvent,
    beginCartSubmission,
    updateCartSubmission,
    completeCartSubmission,
    
    // ODIN logging
    logOdin,
    
    // Failed order recovery
    captureFailedOrder,
    saveCartSnapshot,
    deleteCartSnapshotsForUser,
    
    // Performance monitoring
    logPerformance,
    measurePerformance,
    logPageLoad,
    
    // Error tracking
    logError,
    logCriticalError,
    
    // User interactions
    logInteraction,
    logButtonClick,
    logPageView,
    logFeatureUsage,
    
    // API tracking
    logAPICall,
    trackAPICall,
    
    // Session tracking
    initSession,
    getSessionId,
    getSessionDuration,
    
    // Constants
    LOG_LEVELS,
    COLLECTIONS
};


